import { useState, useEffect, useCallback } from 'react';
import { Clock } from 'lucide-react';

interface CountdownTimerProps {
  /** When true, shows "Claim Now!" instead of countdown */
  canClaimNow?: boolean;
  /** Optional className for styling */
  className?: string;
}

/**
 * Countdown timer that shows time remaining until midnight Central Time.
 * Daily bonus resets at midnight Central Time.
 */
export const CountdownTimer = ({ canClaimNow = false, className = '' }: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  const calculateTimeUntilMidnightCentral = useCallback(() => {
    const now = new Date();
    
    // Get current time in Central Time
    const centralTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    
    // Calculate midnight Central Time tomorrow
    const midnightCentral = new Date(centralTime);
    midnightCentral.setDate(midnightCentral.getDate() + 1);
    midnightCentral.setHours(0, 0, 0, 0);
    
    // Calculate difference in milliseconds
    const diff = midnightCentral.getTime() - centralTime.getTime();
    
    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds };
  }, []);

  useEffect(() => {
    // Initial calculation
    setTimeLeft(calculateTimeUntilMidnightCentral());

    // Update every second
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeUntilMidnightCentral());
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateTimeUntilMidnightCentral]);

  const formatNumber = (num: number) => num.toString().padStart(2, '0');

  if (canClaimNow) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="relative">
          <div className="absolute inset-0 bg-amber-400/30 rounded-full blur-md animate-pulse" />
          <Clock className="h-5 w-5 text-amber-400 relative" />
        </div>
        <span className="text-sm font-bold text-amber-400 animate-pulse">
          Ready to Claim!
        </span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center gap-1 sm:gap-2 ${className}`}>
      <div className="flex items-center gap-1.5 text-muted-foreground/70">
        <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
        <span className="text-[10px] sm:text-xs font-medium uppercase tracking-wider">Next reward in</span>
      </div>
      
      <div className="flex items-center gap-0.5 sm:gap-1">
        {/* Hours */}
        <div className="flex flex-col items-center">
          <div className="bg-gradient-to-b from-muted/80 to-muted/40 border border-border/50 rounded-md sm:rounded-lg px-2 sm:px-3 py-1 sm:py-2 min-w-[40px] sm:min-w-[52px]">
            <span className="text-lg sm:text-2xl font-black text-foreground tabular-nums">
              {formatNumber(timeLeft.hours)}
            </span>
          </div>
          <span className="text-[8px] sm:text-[10px] text-muted-foreground/50 mt-0.5 sm:mt-1 uppercase">hrs</span>
        </div>
        
        <span className="text-base sm:text-xl font-bold text-muted-foreground/30 pb-3 sm:pb-4">:</span>
        
        {/* Minutes */}
        <div className="flex flex-col items-center">
          <div className="bg-gradient-to-b from-muted/80 to-muted/40 border border-border/50 rounded-md sm:rounded-lg px-2 sm:px-3 py-1 sm:py-2 min-w-[40px] sm:min-w-[52px]">
            <span className="text-lg sm:text-2xl font-black text-foreground tabular-nums">
              {formatNumber(timeLeft.minutes)}
            </span>
          </div>
          <span className="text-[8px] sm:text-[10px] text-muted-foreground/50 mt-0.5 sm:mt-1 uppercase">min</span>
        </div>
        
        <span className="text-base sm:text-xl font-bold text-muted-foreground/30 pb-3 sm:pb-4">:</span>
        
        {/* Seconds */}
        <div className="flex flex-col items-center">
          <div className="bg-gradient-to-b from-muted/80 to-muted/40 border border-border/50 rounded-md sm:rounded-lg px-2 sm:px-3 py-1 sm:py-2 min-w-[40px] sm:min-w-[52px]">
            <span className="text-lg sm:text-2xl font-black text-foreground tabular-nums">
              {formatNumber(timeLeft.seconds)}
            </span>
          </div>
          <span className="text-[8px] sm:text-[10px] text-muted-foreground/50 mt-0.5 sm:mt-1 uppercase">sec</span>
        </div>
      </div>
    </div>
  );
};

